import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { selectVisibleRows, useProjectStore, type Density } from "@/store/project-store";
import { WbsGrid } from "@/features/wbs-grid/WbsGrid";
import { GanttChart } from "@/features/gantt/GanttChart";
import { TaskContextMenu } from "./TaskContextMenu";
import { COLS } from "@/features/wbs-grid/WbsGrid";
import {
  clampGridWidth,
  GRID_DRAG_THRESHOLD_PX,
  GRID_MAX_WIDTH,
  gridColumnStops,
  gridFullWidth,
  gridMinWidth,
  nextGridWidthOnDoubleClick,
  nextGridWidthOnKey,
  snapGridWidthToColumn,
} from "./grid-splitter";

export const HEADER_H = 44;
export const ROW_H: Record<Density, number> = { compact: 24, default: 28, comfortable: 32 };

export interface RowsViewport {
  rows: string[];
  rowHeight: number;
  start: number;
  end: number; // exclusive
  scrollTop: number;
  scrollLeft: number;
  viewportHeight: number;
  viewportWidth: number;
}

export function Workspace() {
  const derived = useProjectStore((s) => s.derived);
  const collapsed = useProjectStore((s) => s.collapsed);
  const density = useProjectStore((s) => s.ui.density);
  const gridWidth = useProjectStore((s) => s.ui.gridWidth);
  const scheduleView = useProjectStore((s) => s.ui.scheduleView);
  const hiddenColumns = useProjectStore((s) => s.ui.hiddenColumns);
  const setUI = useProjectStore((s) => s.setUI);
  const focusId = useProjectStore((s) => s.focusId);
  const minWidth = useMemo(() => gridMinWidth(hiddenColumns), [hiddenColumns]);
  const columnStops = useMemo(() => gridColumnStops(COLS, hiddenColumns), [hiddenColumns]);
  const fullWidth = useMemo(() => gridFullWidth(columnStops, minWidth), [columnStops, minWidth]);

  const rows = useMemo(() => selectVisibleRows({ derived, collapsed }), [derived, collapsed]);
  const rowHeight = ROW_H[density];

  const gridRef = useRef<HTMLDivElement>(null);
  const ganttRef = useRef<HTMLDivElement>(null);
  const syncing = useRef(false);
  const [scrollTop, setScrollTop] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(600);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [splitterDragging, setSplitterDragging] = useState(false);

  const onScroll = useCallback((source: "grid" | "gantt") => {
    const from = source === "grid" ? gridRef.current : ganttRef.current;
    const to = source === "grid" ? ganttRef.current : gridRef.current;
    if (!from) return;
    if (source === "gantt") setScrollLeft(from.scrollLeft);
    setScrollTop(from.scrollTop);
    if (!to) return;
    if (syncing.current) return;
    syncing.current = true;
    if (to.scrollTop !== from.scrollTop) to.scrollTop = from.scrollTop;
    requestAnimationFrame(() => {
      syncing.current = false;
    });
  }, []);

  useLayoutEffect(() => {
    const el = scheduleView === "sheet" ? gridRef.current : ganttRef.current;
    if (!el) return;
    const syncSize = () => {
      setViewportHeight(el.clientHeight);
      setViewportWidth(el.clientWidth);
    };
    const ro = new ResizeObserver(syncSize);
    ro.observe(el);
    syncSize();
    return () => ro.disconnect();
  }, [scheduleView]);

  // keep focused row visible
  useEffect(() => {
    if (!focusId) return;
    const idx = rows.indexOf(focusId);
    if (idx < 0) return;
    const el = scheduleView === "sheet" ? gridRef.current : ganttRef.current;
    if (!el) return;
    const top = idx * rowHeight;
    const bottom = top + rowHeight;
    const visTop = el.scrollTop;
    const visBottom = el.scrollTop + el.clientHeight - HEADER_H;
    if (top < visTop) el.scrollTop = top;
    else if (bottom > visBottom) el.scrollTop = bottom - (el.clientHeight - HEADER_H);
  }, [focusId, rows, rowHeight, scheduleView]);

  const overscan = 6;
  const start = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
  const end = Math.min(rows.length, Math.ceil((scrollTop + viewportHeight) / rowHeight) + overscan);
  const viewport: RowsViewport = { rows, rowHeight, start, end, scrollTop, scrollLeft, viewportHeight, viewportWidth };

  // Keep a previously collapsed width legal when WBS (or other identity cols) become visible again.
  useEffect(() => {
    const next = clampGridWidth(gridWidth, minWidth);
    if (next !== gridWidth) setUI({ gridWidth: next });
  }, [gridWidth, minWidth, setUI]);

  // splitter
  const dragging = useRef<{ startX: number; startW: number } | null>(null);
  const onSplitterDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    dragging.current = { startX: e.clientX, startW: gridWidth };
    setSplitterDragging(true);
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };
  const onSplitterMove = (e: React.PointerEvent) => {
    if (!dragging.current) return;
    const w = clampGridWidth(dragging.current.startW + e.clientX - dragging.current.startX, minWidth);
    setUI({ gridWidth: w });
  };
  const onSplitterUp = (e: React.PointerEvent) => {
    const drag = dragging.current;
    dragging.current = null;
    setSplitterDragging(false);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    if (!drag) return;
    const raw = clampGridWidth(drag.startW + e.clientX - drag.startX, minWidth);
    if (Math.abs(raw - drag.startW) <= GRID_DRAG_THRESHOLD_PX) return;
    const snapped = snapGridWidthToColumn(raw, columnStops, minWidth);
    if (snapped !== gridWidth) setUI({ gridWidth: snapped });
  };
  const onSplitterDoubleClick = () => {
    setUI({ gridWidth: nextGridWidthOnDoubleClick(gridWidth, minWidth, fullWidth) });
  };
  const onSplitterKeyDown = (e: React.KeyboardEvent) => {
    const next = nextGridWidthOnKey(gridWidth, e.key, minWidth, columnStops);
    if (next == null) return;
    e.preventDefault();
    setUI({ gridWidth: next });
  };

  return (
    <TaskContextMenu>
    <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden bg-card">
      <div
        ref={gridRef}
        onScroll={() => onScroll("grid")}
        className={`relative h-full min-h-0 overflow-auto ${scheduleView === "gantt" ? "no-scrollbar shrink-0 border-r border-border" : "thin-scrollbar min-w-0 flex-1"}`}
        style={scheduleView === "gantt" ? { width: gridWidth } : undefined}
      >
        <WbsGrid viewport={viewport} paneWidth={scheduleView === "sheet" ? viewportWidth : undefined} />
      </div>
      {scheduleView === "gantt" && (
        <>
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="작업 목록과 간트 차트 너비 조절. 더블클릭하면 작업 열만 보기와 켜 둔 열 전체를 전환합니다."
            aria-valuemin={minWidth}
            aria-valuemax={GRID_MAX_WIDTH}
            aria-valuenow={Math.round(gridWidth)}
            title="드래그하면 열 끝에 붙습니다 · 더블클릭으로 작업 열만 보기 / 켜 둔 열 전체"
            tabIndex={0}
            onPointerDown={onSplitterDown}
            onPointerMove={onSplitterMove}
            onPointerUp={onSplitterUp}
            onPointerCancel={onSplitterUp}
            onDoubleClick={onSplitterDoubleClick}
            onKeyDown={onSplitterKeyDown}
            className="group relative z-10 -mx-[5px] w-[10px] shrink-0 touch-none cursor-col-resize select-none focus:outline-none"
          >
            <div
              className={`absolute inset-y-0 left-1/2 -translate-x-1/2 transition-colors ${
                splitterDragging ? "w-0.5 bg-tint" : "w-0.5 bg-tint/25 group-hover:bg-tint/70 group-focus-visible:bg-tint/70"
              }`}
            />
          </div>
          <div
            ref={ganttRef}
            onScroll={() => onScroll("gantt")}
            className="thin-scrollbar relative min-w-0 flex-1 overflow-auto"
          >
            <GanttChart viewport={viewport} scrollContainer={ganttRef} />
          </div>
        </>
      )}
    </div>
    </TaskContextMenu>
  );
}
